import { AppState, Platform } from 'react-native';
import { NEW_NOTIFICATION } from '@reduxLocal/actions/types';
import { store } from '@reduxLocal/persistState';
import notifee, { EventType, TimestampTrigger, TriggerType } from '@notifee/react-native';
import App from 'App';

/**
 * Represents a notice or notification that can be dispatched within the system.
 * This interface defines the structure of a notice, including its content, metadata, and optional properties.
 */
interface NoticeDispatcher {
	/**
	 * The title of the notice.
	 */
	title: string;

	/**
	 * The body or main content of the notice.
	 */
	body: string;

	/**
	 * (Optional) The stack trace or additional debugging information related to the notice.
	 */
	stack?: string;

	/**
	 * (Optional) The screen or context where the notice is relevant.
	 */
	screen?: string;

	/**
	 * (Optional) A URL or path to an image associated with the notice.
	 */
	image?: string;

	/**
	 * The timestamp indicating when the notice was created or dispatched.
	 */
	timestamp: number;

	/**
	 * (Optional) The type or category of the notice (e.g., error, warning, info).
	 */
	type?: string;

	/**
	 * (Optional) Additional data to pass to the screen (e.g., integration name for search).
	 */
	integrationName?: string;
}

const noticeDispatcher = async ({
	title,
	body,
	stack,
	image,
	screen,
	timestamp,
	type = 'warning',
	integrationName,
}: NoticeDispatcher) => {
	//Check if app is in foreground or background
	const appState = AppState.currentState;
	const safeTimestamp =
		typeof timestamp === 'number' && !Number.isNaN(timestamp)
			? timestamp
			: Date.now();

	if (!title) {
		console.warn('noticeDispatcher called without title, skipping');
		return;
	}

	const payload = {
		_id: `${title}-${safeTimestamp}`,
		title,
		body,
		image,
		stack,
		screen,
		timestamp: safeTimestamp,
		type,
		...(integrationName && { integrationName }),
	};

	if (appState === 'active') {
		store.dispatch({
			type: NEW_NOTIFICATION,
			payload,
		});
	} else {
		let channelId = 'notification';
		if (Platform.OS === 'android') {
			const existingChannels = await notifee.getChannels();
			const channelExists = existingChannels.some(
				(channel) => channel.id === channelId
			);

			if (!channelExists) {
				channelId = await notifee.createChannel({
					id: channelId,
					name: 'Notification Channel',
				});
			}
		}
		console.log('aPstate', AppState.currentState);
		// If app is in foreground, do not show system notification
		if (AppState.currentState === 'active') {
			store.dispatch({
				type: NEW_NOTIFICATION,
				payload,
			});
		} else {
			const date = new Date(Date.now() + 1000); // 1 seconds in the future
			const trigger: TimestampTrigger = {
				type: TriggerType.TIMESTAMP,
				timestamp: date.getTime(), // Time in milliseconds
			};
			if (Platform.OS === 'android') {
				await notifee.createTriggerNotification(
					{
						id: `${title}-${safeTimestamp}`,
						title,
						body,
						android: {
							channelId,
							importance: notifee.AndroidImportance.HIGH,
						},
					},
					trigger
				);
			} else {
				await notifee.createTriggerNotification(
					{
						id: `${title}-${safeTimestamp}`,
						title,
						body,
					},
					trigger
				);
			}
		}
	}
};
export default noticeDispatcher;
